<script>
  import { onMount, tick } from 'svelte';
  import { page } from '$app/stores';
  
  // Props for the search bar
  export let placeholder = "Search...";
  export let value = "";
  export let minimal = false; // If true, renders a more compact version
  export let dark = false; // For dark mode support
  export let showSuggestions = true; // Enable/disable suggestions
  
  let searchInput;
  let focused = false;
  let loading = false;
  let suggestions = [];
  let selectedIndex = -1;
  let debounceTimer;
  
  // Get initial query from URL if on search page
  onMount(() => {
    if ($page.url.pathname === '/search') {
      value = $page.url.searchParams.get('q') || '';
    }
  });
  
  // Handle input changes
  async function handleInput() {
    if (!showSuggestions) return;
    
    clearTimeout(debounceTimer);
    
    // Don't fetch suggestions for empty queries
    if (!value.trim()) {
      suggestions = [];
      return;
    }
    
    // Debounce API calls
    debounceTimer = setTimeout(async () => {
      loading = true;
      try {
        const response = await fetch(`/api/search/suggestions?q=${encodeURIComponent(value)}&limit=5`);
        const data = await response.json();
        suggestions = data.suggestions;
        selectedIndex = -1;
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      } finally {
        loading = false;
      }
    }, 300);
  }
  
  // Handle focus
  function handleFocus() {
    focused = true;
    if (value && showSuggestions) {
      handleInput();
    }
  }
  
  // Handle blur
  function handleBlur() {
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => {
      focused = false;
    }, 200);
  }
  
  // Handle keyboard navigation
  function handleKeydown(event) {
    if (!showSuggestions || suggestions.length === 0) return;
    
    // Arrow down
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      selectedIndex = (selectedIndex + 1) % suggestions.length;
    }
    // Arrow up
    else if (event.key === 'ArrowUp') {
      event.preventDefault();
      selectedIndex = selectedIndex <= 0 ? suggestions.length - 1 : selectedIndex - 1;
    }
    // Enter
    else if (event.key === 'Enter' && selectedIndex >= 0) {
      event.preventDefault();
      value = suggestions[selectedIndex];
      suggestions = [];
      searchInput.form.submit();
    }
    // Escape
    else if (event.key === 'Escape') {
      suggestions = [];
      searchInput.blur();
    }
  }
  
  // Apply suggestion
  function applySuggestion(suggestion) {
    value = suggestion;
    suggestions = [];
    // Focus input to allow immediate submission
    searchInput.focus();
  }

  // Handle keyboard events on suggestions
  function handleSuggestionKeydown(event, suggestion) {
    // Apply suggestion on Enter or Space
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      applySuggestion(suggestion);
    }
  }
</script>

<div class="search-container {minimal ? 'minimal' : ''} {dark ? 'dark' : ''}">
  <form action="/search" method="GET" class="search-form">
    <div class="search-input-wrapper">
      <input 
        bind:this={searchInput}
        type="text" 
        name="q" 
        bind:value={value} 
        placeholder={placeholder}
        aria-label="Search query"
        on:input={handleInput}
        on:focus={handleFocus}
        on:blur={handleBlur}
        on:keydown={handleKeydown}
        autocomplete="off"
      />
      <button type="submit" aria-label="Search">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
      </button>
      
      {#if loading}
        <div class="search-spinner" aria-hidden="true"></div>
      {/if}
    </div>
    
    {#if showSuggestions && focused && suggestions.length > 0}
      <ul class="search-suggestions">
        {#each suggestions as suggestion, i}
          <button
          type="button"
          class={`suggestion-item ${i === selectedIndex ? 'selected' : ''}`}
            on:mousedown={() => applySuggestion(suggestion)}
            on:keydown={(e) => handleSuggestionKeydown(e, suggestion)}
          >
            <span class="suggestion-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </span>
            {suggestion}
          </button>
        {/each}
      </ul>
    {/if}
  </form>
</div>

<style>
  .search-container {
    position: relative;
    width: 100%;
  }
  
  .search-form {
    width: 100%;
  }
  
  .search-input-wrapper {
    display: flex;
    align-items: center;
    position: relative;
  }
  
  input {
    flex: 1;
    padding: 0.75rem 1rem;
    border: 2px solid #ddd;
    border-radius: 4px 0 0 4px;
    font-size: 1rem;
    outline: none;
    transition: border-color 0.2s ease;
  }
  
  input:focus {
    border-color: #4caf50;
  }
  
  button {
    padding: 0.75rem 1rem;
    background-color: #4caf50;
    color: white;
    border: none;
    border-radius: 0 4px 4px 0;
    cursor: pointer;
    transition: background-color 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  button:hover {
    background-color: #45a049;
  }
  
  /* Loading spinner */
  .search-spinner {
    position: absolute;
    right: 4rem;
    width: 18px;
    height: 18px;
    border: 2px solid #ddd;
    border-top-color: #4caf50;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  /* Suggestions dropdown */
  .search-suggestions {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    max-height: 300px;
    overflow-y: auto;
    background-color: white;
    border: 1px solid #ddd;
    border-top: none;
    border-radius: 0 0 4px 4px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    z-index: 10;
    margin: 0;
    padding: 0;
    list-style: none;
  }
  
  .search-suggestions button.suggestion-item {
    padding: 0.75rem 1rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    text-align: left;
    background: none;
    border: none;
    font-size: 1rem;
  }
  
  .search-suggestions button.suggestion-item:hover,
  .search-suggestions button.suggestion-item.selected {
    background-color: #f8f9fa;
  }
  
  .suggestion-icon {
    color: #777;
    display: flex;
    align-items: center;
  }
  
  /* Minimal variant */
  .search-container.minimal input {
    padding: 0.5rem 0.75rem;
    font-size: 0.9rem;
    border-width: 1px;
  }
  
  .search-container.minimal button {
    padding: 0.5rem 0.75rem;
  }
  
  .search-container.minimal .search-spinner {
    width: 16px;
    height: 16px;
    right: 3.5rem;
  }
  
  /* Dark mode variant */
  .search-container.dark input {
    background-color: #2c2c2c;
    border-color: #444;
    color: #fff;
  }
  
  .search-container.dark input::placeholder {
    color: #aaa;
  }
  
  .search-container.dark button {
    background-color: #555;
  }
  
  .search-container.dark button:hover {
    background-color: #666;
  }
  
  .search-container.dark .search-suggestions {
    background-color: #2c2c2c;
    border-color: #444;
  }
  
  .search-container.dark .search-suggestions button.suggestion-item:hover,
  .search-container.dark .search-suggestions button.suggestion-item.selected {
    background-color: #3a3a3a;
  }
</style>